import { Grid, Stack, Typography } from '@mui/material';
import { ChartCard } from '../chart-card';
import { RankBar, ShareBar } from '../charts';
import { TrendPanel } from '../trend-panel';
import { ReportTable } from '../report-table';
import { inr, num, pct } from '../report-theme';
import { rankedCategories, repeatRate, ReportModel } from '../report-data';

interface TabProps {
	report: ReportModel;
	hasAdvancedAnalytics: boolean;
}

export const OverviewTab = ({ report, hasAdvancedAnalytics }: TabProps) => {
	const services = rankedCategories(report.serviceCategories).slice(0, 6);
	const staff = [...report.staffPerformance].sort((a, b) => (b.totalRevenue ?? 0) - (a.totalRevenue ?? 0));

	return (
		<Grid container spacing={3}>
			{hasAdvancedAnalytics && (
				<Grid item xs={12}>
					<TrendPanel report={report} height={300} />
				</Grid>
			)}

			<Grid item xs={12} md={4}>
				<ChartCard
					title="Revenue mix"
					subtitle="Where the money came from"
				>
					<ShareBar
						slices={[
							{ label: 'Services', value: report.totalServiceSales },
							{ label: 'Products', value: report.totalProductSales },
						]}
					/>
				</ChartCard>
			</Grid>

			<Grid item xs={12} md={4}>
				<ChartCard title="Payment methods" subtitle="How customers paid">
					<ShareBar
						slices={[
							{ label: 'Cash', value: report.paymentReport.cash },
							{ label: 'Card', value: report.paymentReport.card },
							{ label: 'UPI', value: report.paymentReport.upi },
						]}
					/>
				</ChartCard>
			</Grid>

			<Grid item xs={12} md={4}>
				<ChartCard
					title="Customers"
					subtitle={`${pct(repeatRate(report), 0)} of visits were returning customers`}
				>
					<ShareBar
						slices={[
							{ label: 'Returning', value: report.totalExistingCustomers },
							{ label: 'New', value: report.totalNewCustomers },
						]}
						valueFormatter={(value) => num(value)}
					/>
				</ChartCard>
			</Grid>

			{hasAdvancedAnalytics && (
				<>
					<Grid item xs={12} md={6}>
						<ChartCard
							title="Top services"
							subtitle="Highest earning services in this period"
							isEmpty={services.length === 0}
							emptyMessage="No service revenue in the selected period."
						>
							<RankBar
								categories={services.map((row) => row.name)}
								values={services.map((row) => row.revenue)}
							/>
						</ChartCard>
					</Grid>

					<Grid item xs={12} md={6}>
						<ChartCard
							title="Team leaderboard"
							subtitle="Revenue booked per staff member"
							isEmpty={staff.length === 0}
							emptyMessage="No staff activity in the selected period."
						>
							<ReportTable
								columns={[
									{
										title: 'Staff',
										render: (row: any) => (
											<Stack>
												<Typography variant="body2" sx={{ fontWeight: 500 }}>
													{row.userName}
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{`${num(row.orderCount)} order${row.orderCount === 1 ? '' : 's'}`}
												</Typography>
											</Stack>
										),
									},
									{
										title: 'Avg order',
										align: 'right',
										render: (row: any) => inr(row.averageOrderValue),
									},
									{
										title: 'Revenue',
										align: 'right',
										render: (row: any) => (
											<Typography variant="body2" sx={{ fontWeight: 600 }}>
												{inr(row.totalRevenue)}
											</Typography>
										),
									},
								]}
								rows={staff}
								emptyMessage="No staff activity in the selected period."
							/>
						</ChartCard>
					</Grid>
				</>
			)}
		</Grid>
	);
};
